import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import countriesListJson from '../../../../../assets/data/countries_codes_with_flags.json'
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule} from "@angular/forms";
import {Country} from "../../../../_models/company";
import {CustomSelectModule} from "../inputs/custom-select/custom-select.module";
import {getNorthAmericaCountryByPhone, NORTH_AMERICA_DIAL_CODE} from "../../../../_util/phone-country";

// Lower rank = better match; null = no match
function rankCountry(country: Country, query: string, isDigits: boolean): number | null {
  if (isDigits) {
    const dial = country.dial_code.replace('+', '');
    if (dial === query) return 0;
    // Closer dial codes first (+38 before +380 before +3800)
    if (dial.startsWith(query)) return 1 + (dial.length - query.length) / 10;
    return null;
  }

  const name = country.name.toLowerCase();
  if (name === query || country.code.toLowerCase() === query) return 0;
  if (name.startsWith(query)) return 1;
  if (name.split(/[\s,()-]+/).some(word => word.startsWith(query))) return 2;
  if (name.includes(query)) return 3;
  return null;
}

function filterAndRankCountries(countries: Country[], rawQuery: string): Country[] {
  const query = rawQuery.trim().toLowerCase().replace(/^\+/, '');
  if (!query) return countries;

  const isDigits = /^\d+$/.test(query);
  return countries
    .map((country, index) => ({country, index, rank: rankCountry(country, query, isDigits)}))
    .filter(item => item.rank !== null)
    .sort((a, b) => a.rank - b.rank || (isDigits ? Number(a.country.dial_code.slice(1)) - Number(b.country.dial_code.slice(1)) : 0) || a.index - b.index)
    .map(item => item.country);
}

@Component({
    selector: 'app-countries-code-select',
    templateUrl: './countries-code-select.component.html',
    styleUrls: ['./countries-code-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountriesCodeSelectComponent),
            multi: true
        }
    ],
    imports: [
        CustomSelectModule,
        ReactiveFormsModule,
    ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class CountriesCodeSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() isError: boolean = false;
  @Input() set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }
  // The phone number next to the select; the USA and Canada share +1, so their number tells which one it is
  @Input() phone: string | null | undefined;
  @Output() change = new EventEmitter<any>();
  // Emits the ISO code of the selected country (the form value only holds the dial code)
  @Output() countryChange = new EventEmitter<string>();

  countriesList: Country[] = [...countriesListJson];
  displayedCountries: Country[] = this.countriesList;
  // Holds the country code (e.g. 'US') because several countries share a dial code
  countryControl = new FormControl<string | null>(null);

  private countryChosenByUser = false;

  onChange: any = () => {};
  onTouch: any = () => {};

  constructor(private readonly cdRef: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.countryControl.valueChanges.subscribe(code => {
      const country = this.findCurrentCountry(code, false);
      if (!country) return;
      this.countryChosenByUser = true;
      this.onChange(country.dial_code);
      this.change.emit(country.dial_code);
      this.countryChange.emit(country.code);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['phone']) return;

    const current = this.selectedCountry;
    if (this.countryChosenByUser || current?.dial_code !== NORTH_AMERICA_DIAL_CODE) return;

    const phoneCountry = getNorthAmericaCountryByPhone(this.phone);
    if (phoneCountry && phoneCountry !== current.code) this.setCountry(phoneCountry);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) this.countryControl.disable({emitEvent: false});
    else this.countryControl.enable({emitEvent: false});
    this.cdRef.markForCheck();
  }

  findCurrentCountry(code: string, isDialCode: boolean) {
    if (isDialCode) return this.countriesList.find(country => country.dial_code === code)
    else return this.countriesList.find(country => country.code === code)
  }

  private setCountry(code: string) {
    this.countryControl.setValue(code, {emitEvent: false});
    this.cdRef.markForCheck();
    // Emitted on the next tick because this can run in the middle of a change detection pass
    Promise.resolve().then(() => this.countryChange.emit(code));
  }

  onSearch(query: string) {
    this.displayedCountries = filterAndRankCountries(this.countriesList, query);
    this.cdRef.markForCheck();
  }

  get selectedCountry(): Country | undefined {
    return this.findCurrentCountry(this.countryControl.value, false);
  }

  getFlagUrl(country: Country) {
    return 'https://hatscripts.github.io/circle-flags/flags/' + country.code.toLowerCase() + '.svg';
  }

  writeValue(value: any): void {
    if (!value) {
      const defaultCountry = this.findCurrentCountry('US', false);
      this.setCountry(defaultCountry.code);
      setTimeout(() => {
        this.onChange(defaultCountry.dial_code);
        this.change.emit(defaultCountry.dial_code);
      });
      return;
    }

    // Keep the selected country when the form writes back the dial code it already holds
    const current = this.findCurrentCountry(this.countryControl.value, false);
    if (current?.dial_code !== value) {
      // +1 is shared by several countries: it is the USA unless the phone number says it is Canada
      const code = value === NORTH_AMERICA_DIAL_CODE
        ? getNorthAmericaCountryByPhone(this.phone) ?? 'US'
        : this.findCurrentCountry(value, true)?.code ?? null;
      this.countryControl.setValue(code, {emitEvent: false});
      if (code) this.setCountry(code);
    }
    this.cdRef.markForCheck();
  }
}
